import { useState, useCallback, useRef } from "react";
import { Checkbox, FormControlLabel, Box, TextField, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import CancelIcon from "@mui/icons-material/Cancel";
import { Menu, MenuItemListWrapper } from "components/OrderSearch/chipDropdownMenu.styles";
import { FilterType } from "components/OrderSearch/filterType";
import { BrandWithProductLines } from "../../../MockModels/MockBrandAndProductLine.ts";
import * as Style from "./MarketingMaterialsBrandProductLineFilter.styles.tsx";

interface MarketingMaterialsBrandProductLineFilterProps {
	selectedItems: string[];
	onChange: (values: string[]) => void;
	dataTestId?: string;
	GTMIdentifier?: string;
	brandAndProductLine?: BrandWithProductLines[];
}

const MarketingMaterialsBrandProductLineFilter = ({
	selectedItems,
	onChange,
	dataTestId,
	GTMIdentifier,
	brandAndProductLine = []
}: MarketingMaterialsBrandProductLineFilterProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [searchValue, setSearchValue] = useState("");
	const anchorRef = useRef<HTMLDivElement>(null);

	const hasSelections = selectedItems.length > 0;

	const filteredData = brandAndProductLine.filter((brand) => {
		const brandMatches = brand.name.toLowerCase().includes(searchValue.toLowerCase());
		const childMatches = brand.productLines.some((pl) => pl.name.toLowerCase().includes(searchValue.toLowerCase()));
		return brandMatches || childMatches;
	});

	// Check if all children of a parent are selected
	const areAllChildrenSelected = (brand: BrandWithProductLines): boolean => {
		if (brand.productLines.length === 0) return selectedItems.includes(brand.id);
		return brand.productLines.every((pl) => selectedItems.includes(pl.id));
	};

	// Check if some (but not all) children are selected
	const areSomeChildrenSelected = (brand: BrandWithProductLines): boolean => {
		if (brand.productLines.length === 0) return false;
		const selectedCount = brand.productLines.filter((pl) => selectedItems.includes(pl.id)).length;
		return selectedCount > 0 && selectedCount < brand.productLines.length;
	};

	const handleParentChange = useCallback(
		(brand: BrandWithProductLines, checked: boolean) => {
			if (brand.productLines.length === 0) {
				// No children - just toggle the parent
				if (checked) {
					onChange([...selectedItems, brand.id]);
				} else {
					onChange(selectedItems.filter((id) => id !== brand.id));
				}
			} else {
				// Has children - select/deselect all children
				const childIds = brand.productLines.map((pl) => pl.id);
				if (checked) {
					const newSelected = [...new Set([...selectedItems, ...childIds])];
					onChange(newSelected);
				} else {
					onChange(selectedItems.filter((id) => !childIds.includes(id)));
				}
			}
		},
		[selectedItems, onChange]
	);

	// Handle child checkbox change
	const handleChildChange = useCallback(
		(childId: string, checked: boolean) => {
			if (checked) {
				onChange([...selectedItems, childId]);
			} else {
				onChange(selectedItems.filter((id) => id !== childId));
			}
		},
		[selectedItems, onChange]
	);

	const handleClear = () => {
		onChange([]);
	};

	const getLabel = () => {
		if (selectedItems.length === 0) {
			return FilterType.BRAND_AND_PRODUCT_LINE;
		}
		if (selectedItems.length === 1) {
			// Find the name of the selected item
			for (const brand of brandAndProductLine) {
				if (brand.id === selectedItems[0]) return brand.name;
				const pl = brand.productLines.find((p) => p.id === selectedItems[0]);
				if (pl) return pl.name;
			}
		}
		return `${FilterType.BRAND_AND_PRODUCT_LINE} (${selectedItems.length})`;
	};

	return (
		<div>
			<Style.StyledFilterChip
				clickable
				ref={anchorRef}
				deleteIcon={
					hasSelections ? (
						<div data-testid="CancelIcon">
							<CancelIcon />
						</div>
					) : undefined
				}
				onDelete={hasSelections ? handleClear : undefined}
				variant="outlined"
				label={getLabel()}
				onClick={() => setIsOpen(!isOpen)}
				icon={<ExpandMoreRounded />}
				color={hasSelections ? "secondary" : "primary"}
				data-testid={dataTestId}
				data-id={GTMIdentifier}
				hasWarning={false}
			/>
			<Menu
				open={isOpen}
				anchorEl={anchorRef.current}
				onClose={() => setIsOpen(false)}
				data-testid="brand-product-line-dropdown-menu"
			>
				{/* Search Input */}
				<Box sx={{ p: 1 }}>
					<TextField
						fullWidth
						size="small"
						placeholder="Search by Product Line"
						value={searchValue}
						onChange={(e) => setSearchValue(e.target.value)}
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<SearchIcon />
								</InputAdornment>
							)
						}}
						data-testid="brand-product-line-search"
					/>
				</Box>

				{/* Hierarchical Checkbox List */}
				<MenuItemListWrapper
					isSearchable
					child={null}
				>
					{filteredData.map((brand) => (
						<Box key={brand.id}>
							{/* Parent Checkbox */}
							<FormControlLabel
								sx={{ ml: 0.5 }}
								control={
									<Checkbox
										checked={areAllChildrenSelected(brand)}
										indeterminate={areSomeChildrenSelected(brand)}
										onChange={(e) => handleParentChange(brand, e.target.checked)}
										data-testid={`brand-checkbox-${brand.id}`}
									/>
								}
								label={brand.name}
							/>

							{/* Children Checkboxes */}
							{brand.productLines.length > 0 && (
								<Box sx={{ ml: 4 }}>
									{brand.productLines
										.filter((pl) =>
											searchValue
												? pl.name.toLowerCase().includes(searchValue.toLowerCase())
												: true
										)
										.map((productLine) => (
											<Box key={productLine.id}>
												<FormControlLabel
													control={
														<Checkbox
															checked={selectedItems.includes(productLine.id)}
															onChange={(e) =>
																handleChildChange(productLine.id, e.target.checked)
															}
															data-testid={`product-line-checkbox-${productLine.id}`}
														/>
													}
													label={productLine.name}
												/>
											</Box>
										))}
								</Box>
							)}
						</Box>
					))}
				</MenuItemListWrapper>
			</Menu>
		</div>
	);
};

export default MarketingMaterialsBrandProductLineFilter;
